import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { loginWorkspace, registerWorkspace, saveBuyerSession } from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

export default function WorkspaceLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("register");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    country: "",
  });

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const goDashboard = (data) => {
    saveBuyerSession(data);
    navigate(
      `/workspace?buyer_id=${data.buyer_id}&workspace_id=${data.workspace_id}`,
      { replace: true }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Email is required.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        const data = await registerWorkspace({
          name: form.name.trim() || undefined,
          email: form.email.trim(),
          company: form.company.trim() || undefined,
          phone: form.phone.trim() || undefined,
          country: form.country.trim() || undefined,
        });
        toast.success("Workspace created.");
        goDashboard(data);
      } else {
        const data = await loginWorkspace({ email: form.email.trim() });
        toast.success("Welcome back.");
        goDashboard(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not continue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen wizard-surface flex flex-col" data-testid={WORKSPACE.login}>
      <header className="glass-header">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-[4.5rem] flex items-center">
          <BrandLogo to="/workspace" variant="default" />
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="card-panel w-full max-w-lg p-8 sm:p-10 shadow-glow">
          <div className="text-center sm:text-left">
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(var(--brand-red))]">
              DeMaxtore Workspace
            </p>
            <h1 className="mt-3 text-3xl font-light text-[hsl(var(--brand-navy))]">
              {mode === "register" ? "Open your workspace" : "Sign in to workspace"}
            </h1>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              Enter your details once. Every new RFQ will use this profile automatically.
            </p>
          </div>

          <div className="mt-8 flex gap-2 p-1 rounded-lg bg-neutral-100">
            <button
              type="button"
              className={`flex-1 py-2 text-sm rounded-md transition-colors ${
                mode === "register"
                  ? "bg-white shadow text-[hsl(var(--brand-navy))] font-medium"
                  : "text-neutral-500"
              }`}
              onClick={() => setMode("register")}
            >
              New account
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm rounded-md transition-colors ${
                mode === "login"
                  ? "bg-white shadow text-[hsl(var(--brand-navy))] font-medium"
                  : "text-neutral-500"
              }`}
              onClick={() => setMode("login")}
            >
              Sign in
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ws-name">Your name</Label>
                  <Input
                    id="ws-name"
                    data-testid={WORKSPACE.registerName}
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="John Smith"
                    className="field-input h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ws-company">Company</Label>
                  <Input
                    id="ws-company"
                    data-testid={WORKSPACE.registerCompany}
                    value={form.company}
                    onChange={(e) => setField("company", e.target.value)}
                    placeholder="Acme Trading Ltd"
                    className="field-input h-11"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ws-phone">WhatsApp / phone</Label>
                    <Input
                      id="ws-phone"
                      data-testid={WORKSPACE.registerPhone}
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="+233 24 123 4567"
                      className="field-input h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ws-country">Country</Label>
                    <Input
                      id="ws-country"
                      value={form.country}
                      onChange={(e) => setField("country", e.target.value)}
                      placeholder="Ghana"
                      className="field-input h-11"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="ws-email">
                Email <span className="text-[hsl(var(--brand-red))]">*</span>
              </Label>
              <Input
                id="ws-email"
                type="email"
                data-testid={WORKSPACE.registerEmail}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="you@company.com"
                className="field-input h-11"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-wizard-primary w-full h-12 mt-2"
              data-testid={WORKSPACE.registerSubmit}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "register" ? (
                "Create workspace"
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
